import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";

import {
  type CreateMedicationsBulk,
  MedicationGroup,
  apiPostMedicationsBulk,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import {
  type SupportedMedications,
  useSupportedMedications,
} from "../../../catalogs/supported-medications";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { NumberInput } from "../../../components/ui-kit/fields/number-input";
import { Radio } from "../../../components/ui-kit/fields/radio";
import { Select } from "../../../components/ui-kit/fields/select";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import {
  RowAccordion,
  useRowAccordion,
} from "../../../components/ui-kit/tables/row-accordion";
import { createRowId } from "../../../utils/utils";
import { MedicationCombobox } from "./medication-combobox";
import {
  endedDateToSubmit,
  MedicationPeriodFields,
} from "./medication-period-fields";

export const AddMedicationsDialog = observer(function AddMedicationsDialog({
  app,
  onClose,
  onSaved,
}: {
  app: AppModel;
  onClose: () => void;
  onSaved: () => void;
}) {
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      className="top-[5dvh] max-h-[90dvh] w-[750px] translate-y-0"
    >
      <AddMedicationsForm app={app} onClose={onClose} onSaved={onSaved} />
    </Dialog>
  );
});

// Rendered inside `Dialog` so the catalog hook suspends into the dialog's own
// Suspense boundary (spinner in-frame) rather than blanking the page behind it.
const AddMedicationsForm = observer(function AddMedicationsForm({
  app,
  onClose,
  onSaved,
}: {
  app: AppModel;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLingui();
  const medications = useSupportedMedications();
  const patient = app.requirePatient();
  const api = app.seerlinqApi;

  const accordion = useRowAccordion(createRow, [createRow()]);

  const hasValidRows =
    accordion.rows.length > 0 &&
    accordion.rows.every((r) => isMedicationRowComplete(medications, r));

  async function save() {
    const items = accordion.rows.map((r) =>
      toMedicationPayload(medications, r, patient.patientId),
    );

    try {
      const response = await apiPostMedicationsBulk({
        body: {
          number_of_datapoints: items.length,
          medications: items,
        },
      });

      if (response.data != null) {
        api.clearResponseCache();
        onSaved();
        onClose();
      }
    } catch (error) {
      window.handleError(error);
    }
  }

  return (
    <>
      <h3 className="mb-6 text-base font-bold">
        <Trans>Add medications</Trans>
      </h3>

      <Form className="flex min-h-0 flex-1 flex-col" onSubmit={() => save()}>
        <div className="min-h-0 overflow-y-auto overflow-x-hidden py-1 px-1">
          <RowAccordion
            {...accordion}
            summaryLabel={(row, index) =>
              row.medication_name
                ? row.medication_name
                : t`Medication ${index + 1}`
            }
            renderRow={(row) => <RowForm row={row} medications={medications} />}
          />
        </div>

        <div className="mt-6 flex justify-center gap-2">
          <Button type="submit" disabled={!hasValidRows}>
            <Trans>Add</Trans>
          </Button>
          <Button
            type="button"
            variant="subtle"
            size="default"
            onClick={onClose}
          >
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </Form>
    </>
  );
});

const RowForm = observer(function RowForm({
  row,
  medications,
}: {
  row: MedicationRow;
  medications: SupportedMedications;
}) {
  const { t } = useLingui();
  const custom = isCustomRow(medications, row);
  const doseUnitDisabled = isDoseUnitDisabled(medications, row);
  // "Other" is a valid group deliberately kept out of the catalog; offer it as a
  // last-resort bucket for a medication that fits no listed group (rare), and
  // label it so its purpose is clear. The submitted value stays "Other".
  const groupOptions: Record<string, string> = {
    ...Object.fromEntries(medications.freeTextGroups.map((g) => [g, g])),
    [MedicationGroup.Other]: t`Other (unclassified)`,
  };

  return (
    <FormTable dataAdd className="max-w-full">
      <FormRow
        label={t`Medication`}
        data={
          <MedicationCombobox
            medications={medications}
            value={row.medication_name}
            onSelect={(group, name) => {
              row.medication_group = group;
              row.medication_name = name;
            }}
            onCustom={(name) => {
              row.medication_name = name;
              row.medication_group = null;
            }}
          />
        }
        mandatory
      />
      {custom && (
        <FormRow
          label={t`Group`}
          data={
            <Select
              model={row}
              attr="medication_group"
              values={groupOptions}
              listTitle={t`Select a group…`}
            />
          }
          mandatory
        />
      )}
      <MedicationPeriodFields model={row} />
      <FormRow
        label={t`Dose`}
        data={
          doseUnitDisabled ? (
            <span className="text-muted-foreground text-sm">
              <Trans>Included in name</Trans>
            </span>
          ) : (
            <NumberInput
              model={row}
              attr="medication_dose"
              min={0}
              width={80}
            />
          )
        }
        mandatory={!doseUnitDisabled}
      />
      <FormRow
        label={t`Unit`}
        data={
          doseUnitDisabled ? (
            <span className="text-muted-foreground text-sm">
              <Trans>Included in name</Trans>
            </span>
          ) : (
            <TextInput model={row} attr="medication_unit" width={80} />
          )
        }
        mandatory={!doseUnitDisabled}
      />
      <FormRow
        label={t`Dosage`}
        data={
          <div className="flex items-center gap-1">
            {[0, 1, 2, 3].map((i) => (
              <NumberInput
                key={i}
                min={0}
                value={row.medication_dosage[i]}
                onChange={(v) => {
                  row.medication_dosage[i] = v ?? 0;
                }}
                width={50}
              />
            ))}
          </div>
        }
        mandatory
      />
      <FormRow
        label={t`Frequency (every N days)`}
        data={
          <NumberInput
            model={row}
            attr="medication_frequency_regular_repeat_every"
            min={1}
          />
        }
      />
      <FormRow
        label={t`Comment`}
        data={
          <TextInput
            model={row}
            attr="comment"
            multiline="autosize"
            width={330}
          />
        }
      />
      <FormRow
        label={t`Add change event`}
        data={
          <Radio
            values={{ no: t`No`, yes: t`Yes` }}
            value={row.medication_change_auto_event ? "yes" : "no"}
            onChange={(v) => {
              row.medication_change_auto_event = v === "yes";
            }}
          />
        }
      />
    </FormTable>
  );
});

type MedicationDosage = [number, number, number, number];

type MedicationRow = {
  __rowId: string;
  medication_group: string | null;
  medication_name: string | null;
  medication_started: string | null;
  medication_ended: string | null;
  medication_has_ended: boolean;
  medication_dose: number | null;
  medication_unit: string | null;
  medication_frequency_regular_repeat_every: number;
  medication_dosage: MedicationDosage;
  medication_change_auto_event: boolean;
  comment: string | null;
};

function createRow(): MedicationRow {
  return observable({
    __rowId: createRowId(),
    medication_group: null,
    medication_name: null,
    medication_started: null,
    medication_ended: null,
    medication_has_ended: false,
    medication_dose: null,
    medication_unit: null,
    medication_frequency_regular_repeat_every: 1,
    medication_dosage: [0, 0, 0, 0] as MedicationDosage,
    medication_change_auto_event: false,
    comment: null,
  });
}

// A free-text medication: named, but not a catalog entry of its group. Such a
// row needs the user to pick the group (the combobox only sets it for matches).
function isCustomRow(
  medications: SupportedMedications,
  row: MedicationRow,
): boolean {
  if (row.medication_name == null) return false;
  return !(
    row.medication_group != null &&
    medications.isKnownMedication(row.medication_group, row.medication_name)
  );
}

// Combination drugs (e.g. Entresto) encode their doses in the name via the
// catalog's `components`, so the free dose/unit inputs don't apply.
function isDoseUnitDisabled(
  medications: SupportedMedications,
  row: MedicationRow,
): boolean {
  if (row.medication_group == null || row.medication_name == null) return false;
  const item = medications.findItem(row.medication_group, row.medication_name);
  return item?.components != null;
}

// Single source of truth for the mandatory-field contract. Dose/unit are
// required except for combination drugs (dose is part of the name); dosage must
// have at least one non-zero value so the all-zero default cannot be submitted.
function isMedicationRowComplete(
  medications: SupportedMedications,
  row: MedicationRow,
): boolean {
  if (!row.medication_group || !row.medication_name) {
    return false;
  }
  if (
    !isDoseUnitDisabled(medications, row) &&
    (row.medication_dose == null || !row.medication_unit)
  ) {
    return false;
  }
  return row.medication_dosage.some((d) => d > 0);
}

function toMedicationPayload(
  medications: SupportedMedications,
  row: MedicationRow,
  patientId: number,
): CreateMedicationsBulk["medications"][number] {
  // Dose/unit inputs are hidden for combination drugs, but the row may still
  // carry values typed before the medication was switched — drop them here so
  // what's sent matches what's shown.
  const doseUnitDisabled = isDoseUnitDisabled(medications, row);

  // Each array element is a discriminated union keyed by medication_group, where
  // the group fixes which name/dose/unit fields are valid. This dialog is group-
  // agnostic (the group is only known at runtime), so the payload can't be
  // narrowed to a single member statically. isMedicationRowComplete has already
  // validated the row, so cast the assembled object to the element type.
  return {
    medication_group: row.medication_group,
    medication_name: row.medication_name,
    medication_started: row.medication_started,
    medication_ended: endedDateToSubmit(row),
    medication_dose: doseUnitDisabled ? null : row.medication_dose,
    medication_unit: doseUnitDisabled ? null : row.medication_unit,
    medication_dosage: row.medication_dosage,
    medication_frequency_regular_repeat_every:
      row.medication_frequency_regular_repeat_every,
    medication_change_auto_event: row.medication_change_auto_event,
    comment: row.comment,
    patient_id: patientId,
  } as CreateMedicationsBulk["medications"][number];
}
